// Firestore Service - All database operations
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  addDoc,
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  onSnapshot,
  serverTimestamp,
  increment
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { COLLECTIONS } from '../utils/constants';

// ============================================
// USER OPERATIONS
// ============================================

export const createUser = async (uid, userData) => {
  try {
    await setDoc(doc(db, COLLECTIONS.USERS, uid), {
      ...userData,
      createdAt: serverTimestamp(),
      status: 'active',
      approvalStatus: 'pending',
      approvedAt: null,
      approvedBy: null,
      totalTokens: 0,
      vipTokens: []
    });
    return { success: true };
  } catch (error) {
    console.error('Error creating user:', error);
    return { success: false, error: error.message };
  }
};

export const getUser = async (uid) => {
  try {
    const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, uid));
    if (userDoc.exists()) {
      return { success: true, data: { id: userDoc.id, ...userDoc.data() } };
    }
    return { success: false, error: 'User not found' };
  } catch (error) {
    console.error('Error getting user:', error);
    return { success: false, error: error.message };
  }
};

export const updateUser = async (uid, updates) => {
  try {
    await updateDoc(doc(db, COLLECTIONS.USERS, uid), updates);
    return { success: true };
  } catch (error) {
    console.error('Error updating user:', error);
    return { success: false, error: error.message };
  }
};

export const getAllUsers = async () => {
  try {
    const usersSnapshot = await getDocs(collection(db, COLLECTIONS.USERS));
    const users = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return { success: true, data: users };
  } catch (error) {
    console.error('Error getting users:', error);
    return { success: false, error: error.message };
  }
};

export const getPendingUsers = async () => {
  try {
    const q = query(
      collection(db, COLLECTIONS.USERS),
      where('approvalStatus', '==', 'pending'),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    const users = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return { success: true, data: users };
  } catch (error) {
    console.error('Error getting pending users:', error);
    return { success: false, error: error.message };
  }
};

export const approveUser = async (uid, adminUid) => {
  try {
    await updateDoc(doc(db, COLLECTIONS.USERS, uid), {
      approvalStatus: 'approved',
      approvedAt: serverTimestamp(),
      approvedBy: adminUid
    });
    return { success: true };
  } catch (error) {
    console.error('Error approving user:', error);
    return { success: false, error: error.message };
  }
};

export const rejectUser = async (uid) => {
  try {
    await updateDoc(doc(db, COLLECTIONS.USERS, uid), {
      approvalStatus: 'rejected',
      approvedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error rejecting user:', error);
    return { success: false, error: error.message };
  }
};

// ============================================
// VIP TOKEN OPERATIONS
// ============================================

export const createVIPToken = async (tokenData) => {
  try {
    const tokenRef = doc(collection(db, COLLECTIONS.VIP_TOKENS));
    await setDoc(tokenRef, {
      ...tokenData,
      createdAt: serverTimestamp(),
      status: 'active'
    });
    
    // Update user's token count
    await updateDoc(doc(db, COLLECTIONS.USERS, tokenData.userId), {
      totalTokens: increment(1),
      vipTokens: [...(tokenData.vipTokens || []), tokenRef.id]
    });
    
    return { success: true, tokenId: tokenRef.id };
  } catch (error) {
    console.error('Error creating VIP token:', error);
    return { success: false, error: error.message };
  }
};

export const getVIPToken = async (tokenId) => {
  try {
    const tokenDoc = await getDoc(doc(db, COLLECTIONS.VIP_TOKENS, tokenId));
    if (tokenDoc.exists()) {
      return { success: true, data: { id: tokenDoc.id, ...tokenDoc.data() } };
    }
    return { success: false, error: 'Token not found' };
  } catch (error) {
    console.error('Error getting VIP token:', error);
    return { success: false, error: error.message };
  }
};

export const getUserTokens = async (userId) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.VIP_TOKENS),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    const tokens = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return { success: true, data: tokens };
  } catch (error) {
    console.error('Error getting user tokens:', error);
    return { success: false, error: error.message };
  }
};

export const getAllVIPTokens = async () => {
  try {
    const tokensSnapshot = await getDocs(collection(db, COLLECTIONS.VIP_TOKENS));
    const tokens = tokensSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return { success: true, data: tokens };
  } catch (error) {
    console.error('Error getting VIP tokens:', error);
    return { success: false, error: error.message };
  }
};

// ============================================
// PRIZE DRAW OPERATIONS
// ============================================

export const createPrizeDraw = async (drawData) => {
  try {
    const drawRef = doc(collection(db, COLLECTIONS.PRIZE_DRAWS));
    await setDoc(drawRef, {
      ...drawData,
      createdAt: serverTimestamp(),
      status: 'upcoming'
    });
    return { success: true, drawId: drawRef.id };
  } catch (error) {
    console.error('Error creating prize draw:', error);
    return { success: false, error: error.message };
  }
};

export const getPrizeDraw = async (drawId) => {
  try {
    const drawDoc = await getDoc(doc(db, COLLECTIONS.PRIZE_DRAWS, drawId));
    if (drawDoc.exists()) {
      return { success: true, data: { id: drawDoc.id, ...drawDoc.data() } };
    }
    return { success: false, error: 'Draw not found' };
  } catch (error) {
    console.error('Error getting prize draw:', error);
    return { success: false, error: error.message };
  }
};

export const getAllPrizeDraws = async () => {
  try {
    const drawsSnapshot = await getDocs(
      query(collection(db, COLLECTIONS.PRIZE_DRAWS), orderBy('createdAt', 'desc'))
    );
    const draws = drawsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return { success: true, data: draws };
  } catch (error) {
    console.error('Error getting prize draws:', error);
    return { success: false, error: error.message };
  }
};

export const updatePrizeDraw = async (drawId, updates) => {
  try {
    await updateDoc(doc(db, COLLECTIONS.PRIZE_DRAWS, drawId), updates);
    return { success: true };
  } catch (error) {
    console.error('Error updating prize draw:', error);
    return { success: false, error: error.message };
  }
};

// ============================================
// WINNER OPERATIONS
// ============================================

export const createWinner = async (winnerData) => {
  try {
    const winnerRef = doc(collection(db, COLLECTIONS.WINNERS));
    await setDoc(winnerRef, {
      ...winnerData,
      createdAt: serverTimestamp(),
      deliveryStatus: 'pending'
    });
    return { success: true, winnerId: winnerRef.id };
  } catch (error) {
    console.error('Error creating winner:', error);
    return { success: false, error: error.message };
  }
};

export const getAllWinners = async () => {
  try {
    const winnersSnapshot = await getDocs(
      query(collection(db, COLLECTIONS.WINNERS), orderBy('createdAt', 'desc'))
    );
    const winners = winnersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return { success: true, data: winners };
  } catch (error) {
    console.error('Error getting winners:', error);
    return { success: false, error: error.message };
  }
};

export const updateWinner = async (winnerId, updates) => {
  try {
    await updateDoc(doc(db, COLLECTIONS.WINNERS, winnerId), updates);
    return { success: true };
  } catch (error) {
    console.error('Error updating winner:', error);
    return { success: false, error: error.message };
  }
};

// ============================================
// PRODUCT OPERATIONS
// ============================================

export const createProduct = async (productData) => {
  try {
    const productRef = await addDoc(collection(db, COLLECTIONS.PRODUCTS), {
      ...productData,
      status: productData.status || 'coming_soon',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return { success: true, id: productRef.id };
  } catch (error) {
    console.error('Error creating product:', error);
    return { success: false, error: error.message };
  }
};

export const updateProduct = async (productId, updates) => {
  try {
    await updateDoc(doc(db, COLLECTIONS.PRODUCTS, productId), {
      ...updates,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating product:', error);
    return { success: false, error: error.message };
  }
};

export const deleteProduct = async (productId) => {
  try {
    await deleteDoc(doc(db, COLLECTIONS.PRODUCTS, productId));
    return { success: true };
  } catch (error) {
    console.error('Error deleting product:', error);
    return { success: false, error: error.message };
  }
};

export const getAllProducts = async () => {
  try {
    const productsSnapshot = await getDocs(
      query(collection(db, COLLECTIONS.PRODUCTS), orderBy('createdAt', 'desc'))
    );
    const products = productsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return { success: true, data: products };
  } catch (error) {
    console.error('Error fetching products:', error);
    return { success: false, error: error.message };
  }
};

// ============================================
// INQUIRY OPERATIONS
// ============================================

export const createInquiry = async (inquiryData) => {
  try {
    const inquiryRef = doc(collection(db, COLLECTIONS.INQUIRIES));
    await setDoc(inquiryRef, {
      ...inquiryData,
      createdAt: serverTimestamp(),
      status: 'new'
    });
    return { success: true, inquiryId: inquiryRef.id };
  } catch (error) {
    console.error('Error creating inquiry:', error);
    return { success: false, error: error.message };
  }
};

export const getAllInquiries = async () => {
  try {
    const inquiriesSnapshot = await getDocs(
      query(collection(db, COLLECTIONS.INQUIRIES), orderBy('createdAt', 'desc'))
    );
    const inquiries = inquiriesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return { success: true, data: inquiries };
  } catch (error) {
    console.error('Error getting inquiries:', error);
    return { success: false, error: error.message };
  }
};

export const updateInquiry = async (inquiryId, updates) => {
  try {
    await updateDoc(doc(db, COLLECTIONS.INQUIRIES, inquiryId), updates);
    return { success: true };
  } catch (error) {
    console.error('Error updating inquiry:', error);
    return { success: false, error: error.message };
  }
};

// ============================================
// SETTINGS OPERATIONS
// ============================================

export const getSettings = async () => {
  try {
    const settingsDoc = await getDoc(doc(db, COLLECTIONS.SETTINGS, 'app'));
    if (settingsDoc.exists()) {
      return { success: true, data: settingsDoc.data() };
    }
    return { success: false, error: 'Settings not found' };
  } catch (error) {
    console.error('Error getting settings:', error);
    return { success: false, error: error.message };
  }
};

export const updateSettings = async (settings) => {
  try {
    await setDoc(doc(db, COLLECTIONS.SETTINGS, 'app'), settings, { merge: true });
    return { success: true };
  } catch (error) {
    console.error('Error updating settings:', error);
    return { success: false, error: error.message };
  }
};

// ============================================
// REAL-TIME LISTENERS
// ============================================

export const subscribeToUsers = (callback) => {
  const q = query(collection(db, COLLECTIONS.USERS), orderBy('createdAt', 'desc'));
  return onSnapshot(q, (snapshot) => {
    const users = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    callback(users);
  });
};

export const subscribeToPendingUsers = (callback) => {
  const q = query(
    collection(db, COLLECTIONS.USERS),
    where('approvalStatus', '==', 'pending'),
    orderBy('createdAt', 'desc')
  );
  return onSnapshot(q, (snapshot) => {
    const users = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    callback(users);
  });
};

export const subscribeToVIPTokens = (callback) => {
  const q = query(collection(db, COLLECTIONS.VIP_TOKENS), orderBy('createdAt', 'desc'));
  return onSnapshot(q, (snapshot) => {
    const tokens = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    callback(tokens);
  });
};

export const subscribeToWinners = (callback) => {
  const q = query(collection(db, COLLECTIONS.WINNERS), orderBy('createdAt', 'desc'));
  return onSnapshot(q, (snapshot) => {
    const winners = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    callback(winners);
  });
};

export const subscribeToProducts = (callback) => {
  const q = query(collection(db, COLLECTIONS.PRODUCTS), orderBy('createdAt', 'desc'));
  return onSnapshot(q, (snapshot) => {
    const products = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    callback(products);
  });
};

// ============================================
// STATISTICS
// ============================================

export const getStatistics = async () => {
  try {
    const [usersSnap, tokensSnap, winnersSnap] = await Promise.all([
      getDocs(collection(db, COLLECTIONS.USERS)),
      getDocs(collection(db, COLLECTIONS.VIP_TOKENS)),
      getDocs(collection(db, COLLECTIONS.WINNERS))
    ]);

    return {
      success: true,
      data: {
        totalUsers: usersSnap.size,
        totalTokens: tokensSnap.size,
        totalWinners: winnersSnap.size,
        pendingApprovals: usersSnap.docs.filter(doc => doc.data().approvalStatus === 'pending').length
      }
    };
  } catch (error) {
    console.error('Error getting statistics:', error);
    return { success: false, error: error.message };
  }
};

export const updatePublicStats = async (statsData = {}) => {
  try {
    const statsDocRef = doc(db, COLLECTIONS.STATS, 'public');
    await setDoc(statsDocRef, {
      totalRegistrations: statsData.totalRegistrations || 0,
      totalTokensSold: statsData.totalTokensSold || 0,
      totalRevenue: statsData.totalRevenue || 0,
      totalWinners: statsData.totalWinners || 0,
      armyFundDonation: statsData.armyFundDonation || 0,
      approvedUsers: statsData.approvedUsers || 0,
      updatedAt: serverTimestamp()
    }, { merge: true });
    return { success: true };
  } catch (error) {
    console.error('Error updating public stats:', error);
    return { success: false, error: error.message };
  }
};

export const getPublicStats = async () => {
  try {
    const statsDocRef = doc(db, COLLECTIONS.STATS, 'public');
    const snapshot = await getDoc(statsDocRef);
    if (snapshot.exists()) {
      return { success: true, data: { id: snapshot.id, ...snapshot.data() } };
    }
    return { success: false, error: 'STATS_NOT_FOUND' };
  } catch (error) {
    console.error('Error fetching public stats:', error);
    return { success: false, error: error.message };
  }
};

export const subscribeToPublicStats = (callback, errorCallback) => {
  const statsDocRef = doc(db, COLLECTIONS.STATS, 'public');
  return onSnapshot(statsDocRef,
    (snapshot) => {
      if (snapshot.exists()) {
        callback({ id: snapshot.id, ...snapshot.data() });
      } else {
        callback(null);
      }
    },
    (error) => {
      console.error('Error with public stats listener:', error);
      if (typeof errorCallback === 'function') {
        errorCallback(error);
      }
    }
  );
};
